import React, { useEffect, useState } from "react";
import { useLocation, Redirect } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  Container,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Typography,
  Collapse,
} from "@material-ui/core";
import LoopIcon from "@material-ui/icons/Loop";
import SwapVerticalCircleIcon from "@material-ui/icons/SwapVerticalCircle";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

import CoinField from "./CoinField";
import CoinDialog from "./CoinDialog";
import LoadingButton from "../Components/LoadingButton";
import WrongNetwork from "../Components/wrongNetwork";

import {
  getAmountOut,
  getBalanceAndSymbol,
  swapTokens,
  getReserves,
} from "../ethereumFunctions";

import useIcon from "../hooks/useIcon";
import useFormat from "../hooks/useFormat";
import useChangeQuery from "../hooks/useChangeQuery";
import { searchCoin } from "../apiFunctions";

const useStyles = makeStyles((theme) => ({
  paperContainer: {
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  switchButton: {
    zIndex: 1,
    margin: "-16px",
    padding: theme.spacing(0.5),
  },
  fullWidth: {
    width: "100%",
  },
  title: {
    textAlign: "center",
    padding: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  hr: {
    width: "100%",
    color: theme.palette.grey[400],
  },
  balance: {
    padding: theme.spacing(1),
    overflow: "wrap",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  calcRateBox: {
    borderRadius: "14px",
    padding: "10px 15px",
    backgroundColor: theme.palette.background.default,
    textTransform: "uppercase",
  },
  collapseIcon: {
    "&.active": {
      transform: "rotateX(180deg)",
    },
  },
  footer: {
    marginTop: "285px",
  },
}));

function CoinSwapper({ network }) {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const classes = useStyles();
  const [formatReserve] = useFormat();
  const [changeQueryParam] = useChangeQuery();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [checked, setChecked] = useState(false);

  // Stores a record of whether their respective dialog window is open
  const [dialog1Open, setDialog1Open] = useState(false);
  const [dialog2Open, setDialog2Open] = useState(false);
  const [wrongNetworkOpen, setwrongNetworkOpen] = useState(false);

  // Stores data about their respective coin
  const [coin1, setCoin1] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });
  const [coin2, setCoin2] = useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
    decimal: undefined,
  });

  // Stores the current reserves in the liquidity pool between coin1 and coin2
  const [reserves, setReserves] = useState(["0.0", "0.0"]);

  // Stores the current value of their respective text box
  const [field1Value, setField1Value] = useState("");
  const [field2Value, setField2Value] = useState("");

  // Controls the loading button
  const [loading, setLoading] = useState(false);

  // Switches the top and bottom coins, this is called when users hit the swap button or select the opposite
  // token in the dialog (e.g. if coin1 is TokenA and the user selects TokenB when choosing coin2)
  const switchFields = () => {
    setCoin1(coin2);
    setCoin2(coin1);
    setField1Value(field2Value);
    setReserves(reserves.reverse());
    changeQueryParam("swap", coin2.symbol, coin1.symbol);
  };

  // These functions take an HTML event, pull the data out and puts it into a state variable.
  const handleChange = {
    field1: (e) => {
      setField1Value(e.target.value);
    },
  };

  // calc coin1 to coin2 rate
  const calcRate = () => {
    if (parseFloat(field1Value) && parseFloat(field2Value)) {
      return parseFloat(field2Value / field1Value).toPrecision(8);
    }
    return 0;
  };

  // Determines whether the button should be enabled or not
  const isButtonEnabled = () => {
    // If both coins have been selected, and a valid float has been entered which is less than the user's balance, then return true
    const parsedInput1 = parseFloat(field1Value);
    const parsedInput2 = parseFloat(field2Value);
    return (
      coin1.address &&
      coin2.address &&
      !isNaN(parsedInput1) &&
      !isNaN(parsedInput2) &&
      0 < parsedInput1 &&
      parsedInput1 <= coin1.balance
    );
  };

  // Called when the dialog window for coin1 exits
  const onToken1Selected = (address, symbol) => {
    // Close the dialog window
    setDialog1Open(false);

    // If the user inputs the same token, we want to switch the data in the fields
    if (address === coin2.address) {
      switchFields();
    }
    // We only update the values if the user provides a token
    else if (address && symbol) {
      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        address,
        network.wethAddress,
        network.coins
      ).then((data) => {
        setCoin1({
          address: address,
          symbol: symbol,
          balance: data.balance,
          decimal: data.decimal,
        });
        changeQueryParam(
          "swap",
          symbol,
          coin2.symbol || searchParams.get("tokenOut")
        );
      });
    }
  };

  // Called when the dialog window for coin2 exits
  const onToken2Selected = (address, symbol) => {
    // Close the dialog window
    setDialog2Open(false);

    // If the user inputs the same token, we want to switch the data in the fields
    if (address === coin1.address) {
      switchFields();
    }
    // We only update the values if the user provides a token
    else if ((address, symbol)) {
      // Getting some token data is async, so we need to wait for the data to return, hence the promise
      getBalanceAndSymbol(
        network.account,
        address,
        network.wethAddress,
        network.coins
      ).then((data) => {
        setCoin2({
          address: address,
          symbol: symbol,
          balance: data.balance,
          decimal: data.decimal,
        });
        changeQueryParam(
          "swap",
          coin1.symbol || searchParams.get("tokenIn"),
          symbol
        );
      });
    }
  };

  // This method calls when user have query param for select coin
  // method search coin with api
  // and fill the set coin (coin1 or coin2)
  const search_coin = async (coinNumber, searchObject) => {
    let params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchObject)) {
      params.set(key, value);
    }

    await searchCoin(params).then((res) => {
      if (res) {
        if (coinNumber === "tokenIn") {
          onToken1Selected("0x6485E05a01a7E019d3500Bb2321ce7eb10e603f9", [
            "USDT",
            "ECG",
          ]);
        } else {
          onToken2Selected(
            "0x90b07173E43B4ec3435dee3d8F969ac066342AB8",
            "Matic"
          );
        }
      }
    });
  };

  // Calls the swapTokens Ethereum function to make the swap, then resets nessicary state variables
  const swap = async () => {
    console.log("Attempting to swap tokens...");
    setLoading(true);

    await swapTokens(
      coin1.address,
      coin2.address,
      field1Value,
      network.router,
      network.account,
      network.wethAddress,
      (res) => {
        if (res.status) {
          setLoading(false);

          // If the transaction was successful, we clear to input to make sure the user doesn't accidental redo the transfer
          setField1Value("");
          enqueueSnackbar("Transaction Successful", {
            variant: "success",
            autoHideDuration: 5000,
          });
        } else {
          setLoading(false);
          enqueueSnackbar("Transaction Failed (" + res.e.reason + ")", {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      }
    ).catch((e) => {
      setLoading(false);
      enqueueSnackbar("Transaction Failed (" + e.reason + ")", {
        variant: "error",
        autoHideDuration: 5000,
      });
    });
  };

  // The lambdas within these useEffects will be called when a particular dependency is updated. These dependencies
  // are defined in the array of variables passed to the function after the lambda expression. If there are no dependencies
  // the lambda will only ever be called when the component mounts. These are very useful for calculating new values
  // after a particular state change, for example, calculating the new exchange rate whenever the addresses
  // of the two coins change.

  // This hook is called when either of the state variables `coin1.address` or `coin2.address` change.
  // This means that when the user selects a different coin to convert between, or the coins are swapped,
  // the new reserves will be calculated.
  useEffect(() => {
    console.log(
      "Trying to get Reserves between:\n" + coin1.address + "\n" + coin2.address
    );

    if (coin1.address && coin2.address) {
      getReserves(
        coin1.address,
        coin2.address,
        network.factory,
        network.account
      ).then((data) => setReserves(data));
    }
  }, [
    coin1.address,
    coin2.address,
    network.account,
    network.factory,
    network.router,
  ]);

  // This hook is called when either of the state variables `field1Value` `coin1.address` or `coin2.address` change.
  // It attempts to calculate and set the state variable `field2Value`
  // This means that if the user types a new value into the conversion box or the conversion rate changes,
  // the value in the output box will change.
  useEffect(() => {
    if (isNaN(parseFloat(field1Value))) {
      setField2Value("");
    } else if (parseFloat(field1Value) && coin1.address && coin2.address) {
      getAmountOut(coin1.address, coin2.address, field1Value, network.router)
        .then((amount) => setField2Value(amount.toFixed(7)))
        .catch((e) => {
          console.log(e);
          setField2Value("Insufficient");
          enqueueSnackbar("Insufficient liquidity for this trade.", {
            variant: "error",
            autoHideDuration: 5000,
          });
        });
    } else {
      setField2Value("");
    }
  }, [field1Value, coin1.address, coin2.address]);

  // This hook creates a timeout that will run every ~10 seconds, it's role is to check if the user's balance has
  // updated has changed. This allows them to see when a transaction completes by looking at the balance output.
  useEffect(() => {
    const coinTimeout = setTimeout(() => {
      console.log("props: ", network);
      console.log("Checking balances...");

      if (coin1.address && coin2.address && network.account) {
        getReserves(
          coin1.address,
          coin2.address,
          network.factory,
          network.account
        ).then((data) => setReserves(data));
      }

      if (coin1.address && network.account && !wrongNetworkOpen) {
        getBalanceAndSymbol(
          network.account,
          coin1.address,
          network.wethAddress,
          network.coins
        ).then((data) => {
          setCoin1({
            ...coin1,
            balance: data.balance,
          });
        });
      }
      if (coin2.address && network.account && !wrongNetworkOpen) {
        getBalanceAndSymbol(
          network.account,
          coin2.address,
          network.wethAddress,
          network.coins
        ).then((data) => {
          setCoin2({
            ...coin2,
            balance: data.balance,
          });
        });
      }
    }, 10000);

    return () => clearTimeout(coinTimeout);
  });

  // useEffect(() => {
  //   const queryTimeout = setTimeout(() => {
  //     if (network.chainID) {
  //       const tokenIn = searchParams.get("tokenIn");
  //       const tokenOut = searchParams.get("tokenOut");

  //       if (tokenIn) {
  //         search_coin("tokenIn", {
  //           page: 1,
  //           coin: tokenIn,
  //           chainId: network.chainID,
  //         });
  //       }

  //       if (tokenOut) {
  //         search_coin("tokenOut", {
  //           page: 1,
  //           coin: tokenOut,
  //           chainId: network.chainID,
  //         });
  //       }
  //     }
  //   }, 1000);
  //   return () => clearTimeout(queryTimeout);
  // }, [network]);

  return (
    <>
      {network.chainID != 56 ? (
        <div>
          {" "}
          {/* Dialog Windows */}{" "}
          <CoinDialog
            open={dialog1Open}
            onClose={onToken1Selected}
            coins={network.coins}
            account={network.account}
            weth={network.wethAddress}
            chainId={network.chainID}
          />{" "}
          <CoinDialog
            open={dialog2Open}
            onClose={onToken2Selected}
            coins={network.coins}
            account={network.account}
            weth={network.wethAddress}
            chainId={network.chainID}
          />{" "}
          <WrongNetwork open={wrongNetworkOpen} />{" "}
          <Container maxWidth="xs">
            <Paper elevation={0} className={classes.paperContainer}>
              <Typography variant="h6" className={classes.title}>
                Swap Coins{" "}
              </Typography>{" "}
              <Grid
                container
                direction="column"
                alignItems="center"
                spacing={2}
              >
                <Grid item xs={12} className={classes.fullWidth}>
                  <CoinField
                    activeField={true}
                    value={field1Value}
                    onClick={() => setDialog1Open(true)}
                    onChange={handleChange.field1}
                    symbol={coin1.symbol}
                    balance={coin1.balance}
                    decimal={coin1.decimal}
                  />{" "}
                </Grid>{" "}
                <IconButton
                  onClick={switchFields}
                  className={classes.switchButton}
                >
                  <SwapVerticalCircleIcon fontSize="medium" />
                </IconButton>{" "}
                <Grid item xs={12} className={classes.fullWidth}>
                  <CoinField
                    activeField={false}
                    value={field2Value}
                    onClick={() => setDialog2Open(true)}
                    symbol={coin2.symbol}
                    balance={coin2.balance}
                    showMax={false}
                    decimal={coin1.decimal}
                  />{" "}
                </Grid>{" "}
                {!!calcRate() && (
                  <Grid item xs={12} className={classes.fullWidth}>
                    <div className={classes.calcRateBox}>
                      1 {coin1.symbol} = {calcRate()} {coin2.symbol}{" "}
                    </div>{" "}
                  </Grid>
                )}{" "}
                {coin1.address && coin2.address && (
                  <>
                    <Grid item xs={12} className={classes.fullWidth}>
                      <Typography
                        variant="body1"
                        className="d-flex align-items-center justify-content-between pointer"
                        onClick={() => setChecked(!checked)}
                      >
                        Reserves{" "}
                        <KeyboardArrowDownIcon
                          className={`${classes.collapseIcon} ${
                            checked ? "active" : ""
                          }`}
                        />{" "}
                      </Typography>{" "}
                    </Grid>{" "}
                    <Collapse in={checked} className="w-100">
                      <hr className={classes.hr} />{" "}
                      <Typography variant="h6" className="text-center">
                        Reserves{" "}
                      </Typography>{" "}
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Grid item xs={6}>
                          <Typography
                            variant="body1"
                            className={classes.balance}
                          >
                            {" "}
                            {formatReserve(reserves[0], coin1.symbol)}{" "}
                          </Typography>{" "}
                        </Grid>{" "}
                        <Grid item xs={6}>
                          <Typography
                            variant="body1"
                            className={classes.balance}
                          >
                            {" "}
                            {formatReserve(reserves[1], coin2.symbol)}{" "}
                          </Typography>{" "}
                        </Grid>{" "}
                      </Grid>{" "}
                      <hr className={classes.hr} />{" "}
                    </Collapse>{" "}
                  </>
                )}{" "}
                <LoadingButton
                  loading={loading}
                  valid={isButtonEnabled()}
                  success={false}
                  fail={false}
                  onClick={swap}
                >
                  <LoopIcon /> {loading ? "processing" : "Swap"}{" "}
                </LoadingButton>{" "}
              </Grid>{" "}
            </Paper>{" "}
          </Container>{" "}
        </div>
      ) : (
        <Redirect to="/invest" />
      )}
    </>
  );
}

export default CoinSwapper;
